/**
 * Touch double-tap detection for the double-click-to-teleport gesture
 * (issue #156). Pure, DOM/three.js-free logic — CameraRig feeds it raw
 * touch coordinates/timestamps and only calls the surface-pick raycast when
 * `end()` reports a completed double-tap. Kept separate from cameraRig.ts
 * so the tolerance/interval maths are unit-testable in Node without a DOM.
 */
export interface TapPoint {
  x: number;
  y: number;
  time: number;
}

/** Max gap (ms) between two taps for them to count as a double-tap. */
export const DOUBLE_TAP_MAX_INTERVAL_MS = 400;
/** Max on-screen distance (px) between the two taps' start points. */
export const DOUBLE_TAP_MAX_TAP_DISTANCE_PX = 30;
/** Movement (px) during a single touch beyond which it is a drag, not a tap. */
export const TOUCH_DRAG_CANCEL_DISTANCE_PX = 10;

function distance(a: { x: number; y: number }, b: { x: number; y: number }): number {
  return Math.hypot(b.x - a.x, b.y - a.y);
}

/** True if `second` completes a double-tap started by `first`. */
export function isDoubleTap(first: TapPoint, second: TapPoint): boolean {
  const dt = second.time - first.time;
  if (dt < 0 || dt > DOUBLE_TAP_MAX_INTERVAL_MS) return false;
  return distance(first, second) <= DOUBLE_TAP_MAX_TAP_DISTANCE_PX;
}

/**
 * Tracks a single-finger tap sequence and reports when two taps in a row
 * complete a double-tap. A second finger joining (pinch) or movement beyond
 * tolerance during a touch cancels that touch's candidacy — per the issue's
 * predictable-failure table, "pinch or drag between the two taps" must never
 * teleport.
 */
export class DoubleTapTracker {
  private pendingStart: TapPoint | null = null;
  private pendingDragged = false;
  private lastCompletedTap: TapPoint | null = null;

  /** A single touch began. */
  start(point: TapPoint): void {
    this.pendingStart = point;
    this.pendingDragged = false;
  }

  /** The in-flight touch moved; marks it a drag once past tolerance. */
  move(x: number, y: number): void {
    if (!this.pendingStart || this.pendingDragged) return;
    if (distance(this.pendingStart, { x, y }) > TOUCH_DRAG_CANCEL_DISTANCE_PX) this.pendingDragged = true;
  }

  /** A second finger joined, or the gesture is otherwise invalid — reset everything. */
  cancel(): void {
    this.pendingStart = null;
    this.pendingDragged = false;
    this.lastCompletedTap = null;
  }

  /**
   * The touch lifted. Returns the tap point to pick against if this
   * completes a double-tap, else null (and remembers this tap as the first
   * half of a possible next double-tap, unless it was a drag).
   */
  end(_liftPoint: TapPoint): TapPoint | null {
    const start = this.pendingStart;
    const dragged = this.pendingDragged;
    this.pendingStart = null;
    this.pendingDragged = false;
    if (!start || dragged) {
      this.lastCompletedTap = null;
      return null;
    }
    const prior = this.lastCompletedTap;
    if (prior && isDoubleTap(prior, start)) {
      this.lastCompletedTap = null;
      return start;
    }
    this.lastCompletedTap = start;
    return null;
  }
}
